import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { IoClose, IoCheckmark } from 'react-icons/io5';
import manMatches from '../data/man_matches.json';
import womanMatches from '../data/woman_matches.json';
import manPhrases from '../data/phrases.json';
import womanPhrases from '../data/woman_phrases.json';
import './SwipePage.css';
import { usePov } from '../context/PovContext';

// Fisher–Yates shuffle (returns a new array)
function shuffleArray<T>(items: T[]): T[] {
  const arr = [...items];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

const SwipePage: React.FC = () => {
  const { pov } = usePov();
  const baseMatches = pov === 'woman' ? womanMatches : manMatches;
  const basePhrases = pov === 'woman' ? womanPhrases : manPhrases;

  // Randomize order once per POV selection (stable during session)
  const matches = useMemo(() => shuffleArray(baseMatches), [pov]);
  const phrasesArr = useMemo(() => shuffleArray(basePhrases), [pov]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationDirection, setAnimationDirection] = useState<'left' | 'right' | null>(null);
  const [animatingImageIndex, setAnimatingImageIndex] = useState<number | null>(null);

  const currentImage = matches[currentImageIndex % matches.length];
  const nextImage = matches[(currentImageIndex + 1) % matches.length];
  const currentPhrase = phrasesArr[currentPhraseIndex % phrasesArr.length];



  const handleSwipe = useCallback((direction: 'left' | 'right') => {
    if (isAnimating) return;

    setIsAnimating(true);
    setAnimationDirection(direction);
    setAnimatingImageIndex(currentImageIndex); // Store which image is animating out

    // Update phrase immediately
    setCurrentPhraseIndex((prev) => (prev + 1) % phrasesArr.length);

    // After animation completes, update to next image and clean up
    setTimeout(() => {
      setCurrentImageIndex((prev) => (prev + 1) % matches.length); // Update AFTER animation
      setIsAnimating(false);
      setAnimationDirection(null);
      setAnimatingImageIndex(null);
    }, 1000); // Match the 1s CSS animation duration
  }, [isAnimating, currentImageIndex, matches, phrasesArr]);

  // Keyboard support
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        handleSwipe('left');
      } else if (e.key === 'ArrowRight') {
        handleSwipe('right');
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [handleSwipe]);

  return (
    <div className="page-content">
      <div className="swipe-container">
        <div className="card-stack">
          {/* Next image underneath (doesn't change during animation) */}
          <div 
            className="swipe-card next-card"
            style={{ backgroundImage: `url(${nextImage})` }}
          />
          
          {/* Top slot: current image OR animating image */}
          {isAnimating && animatingImageIndex !== null ? (
            <div 
              className={`swipe-card animating-card animate-${animationDirection}`}
              style={{ 
                backgroundImage: `url(${matches[animatingImageIndex]})`
              }}
            />
          ) : (
            <div 
              className="swipe-card current-card"
              style={{ backgroundImage: `url(${currentImage})` }}
            />
          )}
        </div>

        <div className="page-header">
          <p>{currentPhrase}</p>
        </div>

        <div className="swipe-controls">
          <button 
            className="swipe-btn reject-btn"
            onClick={() => handleSwipe('left')}
            disabled={isAnimating}
          >
            {React.createElement(IoClose as React.ComponentType<any>)}
          </button>
          
          <button 
            className="swipe-btn accept-btn"
            onClick={() => handleSwipe('right')}
            disabled={isAnimating}
          >
            {React.createElement(IoCheckmark as React.ComponentType<any>)}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SwipePage;
